import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Building2, MapPin, FolderKanban, Users, BookOpen, Plus,
  Edit, Trash2, Search, AlertCircle, Check, X, RefreshCw,
  Globe, Phone, Mail, FileText, Database
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const AccountingMasterData = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('companies');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  
  // Selected company for filtering
  const [selectedCompany, setSelectedCompany] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchAllData();
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/accounting/companies');
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [branchesRes, locationsRes, projectsRes, clientsRes, ledgersRes] = await Promise.all([
        api.get(`/accounting/branches?company_id=${selectedCompany}`),
        api.get(`/accounting/locations?company_id=${selectedCompany}`),
        api.get(`/accounting/projects?company_id=${selectedCompany}`),
        api.get(`/accounting/clients?company_id=${selectedCompany}`),
        api.get(`/accounting/ledgers?company_id=${selectedCompany}`)
      ]);
      setBranches(branchesRes.data);
      setLocations(locationsRes.data);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setLedgers(ledgersRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const openCreateModal = (type) => {
    setModalType(type);
    setEditingItem(null);
    setFormData(getDefaultFormData(type));
    setShowModal(true);
  };

  const openEditModal = (type, item) => {
    setModalType(type);
    setEditingItem(item);
    setFormData(item);
    setShowModal(true);
  };

  const getDefaultFormData = (type) => {
    const base = { company_id: selectedCompany };
    switch (type) {
      case 'company':
        return { name: '', legal_name: '', code: '', pan: '', tan: '', gstin: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', is_active: true };
      case 'branch':
        return { ...base, name: '', code: '', address: '', city: '', state: '', gstin: '', is_head_office: false, is_active: true };
      case 'location':
        return { ...base, name: '', code: '', address: '', city: '', state: '', area_sqft: '', rent_amount: '', is_active: true };
      case 'project':
        return { ...base, name: '', code: '', description: '', start_date: '', end_date: '', budget_amount: '', status: 'active', is_billable: true, billing_type: 'fixed', is_active: true };
      case 'client':
        return { ...base, name: '', code: '', contact_person: '', email: '', phone: '', gstin: '', pan: '', address: '', city: '', state: '', credit_days: 30, is_active: true };
      case 'ledger':
        return { ...base, name: '', code: '', account_type: 'expense', account_group: 'indirect_expenses', opening_balance: 0, opening_balance_type: 'dr', is_active: true };
      default:
        return base;
    }
  };

  const handleSave = async () => {
    try {
      let endpoint = '';
      let method = editingItem ? 'put' : 'post';
      
      switch (modalType) {
        case 'company':
          endpoint = editingItem ? `/accounting/companies/${editingItem.id}` : '/accounting/companies';
          break;
        case 'branch':
          endpoint = editingItem ? `/accounting/branches/${editingItem.id}` : '/accounting/branches';
          break;
        case 'location':
          endpoint = editingItem ? `/accounting/locations/${editingItem.id}` : '/accounting/locations';
          break;
        case 'project':
          endpoint = editingItem ? `/accounting/projects/${editingItem.id}` : '/accounting/projects';
          break;
        case 'client':
          endpoint = editingItem ? `/accounting/clients/${editingItem.id}` : '/accounting/clients';
          break;
        case 'ledger':
          endpoint = editingItem ? `/accounting/ledgers/${editingItem.id}` : '/accounting/ledgers';
          break;
      }
      
      if (method === 'put') {
        await api.put(endpoint, formData);
      } else {
        await api.post(endpoint, formData);
      }
      
      toast.success(`${modalType} ${editingItem ? 'updated' : 'created'} successfully`);
      setShowModal(false);
      
      // Refresh data
      if (modalType === 'company') {
        fetchCompanies();
      } else {
        fetchAllData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const seedDefaultLedgers = async () => {
    try {
      await api.post(`/accounting/ledgers/seed-defaults/${selectedCompany}`);
      toast.success('Default ledgers created successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create default ledgers');
    }
  };

  const hasAccess = useSectionAccess('/accounting');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Accounting Master Data</h1>
            <p className="text-gray-500">Manage companies, branches, locations, projects, and chart of accounts</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => window.location.href = '/accounting'}>
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Company Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-xs">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Working Company</label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => openCreateModal('company')} className="mt-6">
                <Plus className="h-4 w-4 mr-2" />
                New Company
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="companies" className="flex items-center gap-1">
              <Building2 className="h-4 w-4" /> Companies
            </TabsTrigger>
            <TabsTrigger value="branches" className="flex items-center gap-1">
              <Globe className="h-4 w-4" /> Branches
            </TabsTrigger>
            <TabsTrigger value="locations" className="flex items-center gap-1">
              <MapPin className="h-4 w-4" /> Locations
            </TabsTrigger>
            <TabsTrigger value="projects" className="flex items-center gap-1">
              <FolderKanban className="h-4 w-4" /> Projects
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-1">
              <Users className="h-4 w-4" /> Clients
            </TabsTrigger>
            <TabsTrigger value="ledgers" className="flex items-center gap-1">
              <BookOpen className="h-4 w-4" /> Ledgers
            </TabsTrigger>
          </TabsList>

          {/* Companies Tab */}
          <TabsContent value="companies">
            <Card>
              <CardHeader>
                <CardTitle>Companies ({companies.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Company</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Code</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">GSTIN</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">City</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Status</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {companies.map(company => (
                        <tr key={company.id} className="hover:bg-gray-50">
                          <td className="p-3">
                            <div className="font-medium text-gray-900">{company.name}</div>
                            <div className="text-xs text-gray-500">{company.legal_name}</div>
                          </td>
                          <td className="p-3 text-sm text-gray-600">{company.code}</td>
                          <td className="p-3 text-sm text-gray-600">{company.gstin || '-'}</td>
                          <td className="p-3 text-sm text-gray-600">{company.city || '-'}</td>
                          <td className="p-3 text-center">
                            <Badge className={company.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                              {company.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => openEditModal('company', company)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branches Tab */}
          <TabsContent value="branches">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Branches ({branches.length})</CardTitle>
                <Button onClick={() => openCreateModal('branch')} size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add Branch
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Branch</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Code</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">City</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">GSTIN</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Head Office</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {branches.length === 0 ? (
                        <tr><td colSpan="6" className="p-8 text-center text-gray-500">No branches found</td></tr>
                      ) : branches.map(branch => (
                        <tr key={branch.id} className="hover:bg-gray-50">
                          <td className="p-3 font-medium text-gray-900">{branch.name}</td>
                          <td className="p-3 text-sm text-gray-600">{branch.code}</td>
                          <td className="p-3 text-sm text-gray-600">{branch.city || '-'}</td>
                          <td className="p-3 text-sm text-gray-600">{branch.gstin || '-'}</td>
                          <td className="p-3 text-center">
                            {branch.is_head_office && <Badge className="bg-blue-100 text-blue-700">HO</Badge>}
                          </td>
                          <td className="p-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => openEditModal('branch', branch)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Locations ({locations.length})</CardTitle>
                <Button onClick={() => openCreateModal('location')} size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add Location
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Location</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Code</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">City</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Area (sqft)</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Rent</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {locations.length === 0 ? (
                        <tr><td colSpan="6" className="p-8 text-center text-gray-500">No locations found</td></tr>
                      ) : locations.map(location => (
                        <tr key={location.id} className="hover:bg-gray-50">
                          <td className="p-3 font-medium text-gray-900">{location.name}</td>
                          <td className="p-3 text-sm text-gray-600">{location.code}</td>
                          <td className="p-3 text-sm text-gray-600">{location.city || '-'}</td>
                          <td className="p-3 text-sm text-gray-600 text-right">{location.area_sqft || '-'}</td>
                          <td className="p-3 text-sm text-gray-600 text-right">{location.rent_amount ? `₹${location.rent_amount.toLocaleString()}` : '-'}</td>
                          <td className="p-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => openEditModal('location', location)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Projects ({projects.length})</CardTitle>
                <Button onClick={() => openCreateModal('project')} size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add Project
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Project</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Code</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Period</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Budget</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Status</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {projects.length === 0 ? (
                        <tr><td colSpan="6" className="p-8 text-center text-gray-500">No projects found</td></tr>
                      ) : projects.map(project => (
                        <tr key={project.id} className="hover:bg-gray-50">
                          <td className="p-3">
                            <div className="font-medium text-gray-900">{project.name}</div>
                            <div className="text-xs text-gray-500">{project.description?.substring(0, 50) || ''}</div>
                          </td>
                          <td className="p-3 text-sm text-gray-600">{project.code}</td>
                          <td className="p-3 text-sm text-gray-600">
                            {project.start_date || '-'} - {project.end_date || 'Ongoing'}
                          </td>
                          <td className="p-3 text-sm text-gray-600 text-right">
                            {project.budget_amount ? `₹${project.budget_amount.toLocaleString()}` : '-'}
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={
                              project.status === 'active' ? 'bg-green-100 text-green-700' :
                              project.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              project.status === 'on_hold' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {project.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => openEditModal('project', project)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Clients ({clients.length})</CardTitle>
                <Button onClick={() => openCreateModal('client')} size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add Client
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Client</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Code</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Contact</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">GSTIN</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Credit Days</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {clients.length === 0 ? (
                        <tr><td colSpan="6" className="p-8 text-center text-gray-500">No clients found</td></tr>
                      ) : clients.map(client => (
                        <tr key={client.id} className="hover:bg-gray-50">
                          <td className="p-3 font-medium text-gray-900">{client.name}</td>
                          <td className="p-3 text-sm text-gray-600">{client.code}</td>
                          <td className="p-3 text-sm text-gray-600">{client.contact_person || '-'}</td>
                          <td className="p-3 text-sm text-gray-600">{client.gstin || '-'}</td>
                          <td className="p-3 text-center">{client.credit_days}</td>
                          <td className="p-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => openEditModal('client', client)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ledgers Tab */}
          <TabsContent value="ledgers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Chart of Accounts ({ledgers.length})</CardTitle>
                <div className="flex gap-2">
                  {ledgers.length === 0 && (
                    <Button onClick={seedDefaultLedgers} variant="outline" size="sm">
                      <Database className="h-4 w-4 mr-2" /> Seed Default Ledgers
                    </Button>
                  )}
                  <Button onClick={() => openCreateModal('ledger')} size="sm">
                    <Plus className="h-4 w-4 mr-2" /> Add Ledger
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Ledger Name</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Account Type</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Account Group</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Balance</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {ledgers.length === 0 ? (
                        <tr><td colSpan="5" className="p-8 text-center text-gray-500">No ledgers found. Click "Seed Default Ledgers" to create standard chart of accounts.</td></tr>
                      ) : ledgers.map(ledger => (
                        <tr key={ledger.id} className="hover:bg-gray-50">
                          <td className="p-3">
                            <div className="font-medium text-gray-900">{ledger.name}</div>
                            {ledger.is_system_ledger && <Badge className="bg-gray-100 text-gray-600 text-xs">System</Badge>}
                          </td>
                          <td className="p-3">
                            <Badge className={
                              ledger.account_type === 'asset' ? 'bg-blue-100 text-blue-700' :
                              ledger.account_type === 'liability' ? 'bg-purple-100 text-purple-700' :
                              ledger.account_type === 'income' ? 'bg-green-100 text-green-700' :
                              ledger.account_type === 'expense' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {ledger.account_type}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm text-gray-600 capitalize">{ledger.account_group?.replace(/_/g, ' ')}</td>
                          <td className="p-3 text-sm text-right">
                            <span className={ledger.current_balance_type === 'dr' ? 'text-blue-600' : 'text-red-600'}>
                              ₹{(ledger.current_balance || 0).toLocaleString()} {ledger.current_balance_type?.toUpperCase()}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            {!ledger.is_system_ledger && (
                              <Button size="sm" variant="ghost" onClick={() => openEditModal('ledger', ledger)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit' : 'Create'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Company Form */}
            {modalType === 'company' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Company Name *</label>
                    <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Legal Name *</label>
                    <Input value={formData.legal_name || ''} onChange={(e) => setFormData({...formData, legal_name: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Code *</label>
                    <Input value={formData.code || ''} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">GSTIN</label>
                    <Input value={formData.gstin || ''} onChange={(e) => setFormData({...formData, gstin: e.target.value.toUpperCase()})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">PAN</label>
                    <Input value={formData.pan || ''} onChange={(e) => setFormData({...formData, pan: e.target.value.toUpperCase()})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">TAN</label>
                    <Input value={formData.tan || ''} onChange={(e) => setFormData({...formData, tan: e.target.value.toUpperCase()})} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium">Address</label>
                    <Input value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">City</label>
                    <Input value={formData.city || ''} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">State</label>
                    <Input value={formData.state || ''} onChange={(e) => setFormData({...formData, state: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Phone</label>
                    <Input value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <Input value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                  </div>
                </div>
              </>
            )}

            {/* Branch Form */}
            {modalType === 'branch' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Branch Name *</label>
                  <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Code *</label>
                  <Input value={formData.code || ''} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} />
                </div>
                <div>
                  <label className="text-sm font-medium">City</label>
                  <Input value={formData.city || ''} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">State</label>
                  <Input value={formData.state || ''} onChange={(e) => setFormData({...formData, state: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">GSTIN</label>
                  <Input value={formData.gstin || ''} onChange={(e) => setFormData({...formData, gstin: e.target.value.toUpperCase()})} />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input type="checkbox" checked={formData.is_head_office || false} onChange={(e) => setFormData({...formData, is_head_office: e.target.checked})} />
                  <label className="text-sm">Head Office</label>
                </div>
              </div>
            )}

            {/* Location Form */}
            {modalType === 'location' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Location Name *</label>
                  <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Code *</label>
                  <Input value={formData.code || ''} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} />
                </div>
                <div>
                  <label className="text-sm font-medium">City</label>
                  <Input value={formData.city || ''} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">State</label>
                  <Input value={formData.state || ''} onChange={(e) => setFormData({...formData, state: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Area (sqft)</label>
                  <Input type="number" value={formData.area_sqft || ''} onChange={(e) => setFormData({...formData, area_sqft: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Monthly Rent</label>
                  <Input type="number" value={formData.rent_amount || ''} onChange={(e) => setFormData({...formData, rent_amount: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
            )}

            {/* Project Form */}
            {modalType === 'project' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Project Name *</label>
                  <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Code *</label>
                  <Input value={formData.code || ''} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} />
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Start Date</label>
                  <Input type="date" value={formData.start_date || ''} onChange={(e) => setFormData({...formData, start_date: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">End Date</label>
                  <Input type="date" value={formData.end_date || ''} onChange={(e) => setFormData({...formData, end_date: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Budget Amount</label>
                  <Input type="number" value={formData.budget_amount || ''} onChange={(e) => setFormData({...formData, budget_amount: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <Select value={formData.status || 'active'} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Client Form */}
            {modalType === 'client' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Client Name *</label>
                  <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Code *</label>
                  <Input value={formData.code || ''} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Contact Person</label>
                  <Input value={formData.contact_person || ''} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <Input value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">GSTIN</label>
                  <Input value={formData.gstin || ''} onChange={(e) => setFormData({...formData, gstin: e.target.value.toUpperCase()})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Credit Days</label>
                  <Input type="number" value={formData.credit_days || 30} onChange={(e) => setFormData({...formData, credit_days: parseInt(e.target.value) || 30})} />
                </div>
              </div>
            )}

            {/* Ledger Form */}
            {modalType === 'ledger' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium">Ledger Name *</label>
                  <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Account Type *</label>
                  <Select value={formData.account_type || 'expense'} onValueChange={(v) => setFormData({...formData, account_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asset">Asset</SelectItem>
                      <SelectItem value="liability">Liability</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="equity">Equity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Account Group *</label>
                  <Select value={formData.account_group || 'indirect_expenses'} onValueChange={(v) => setFormData({...formData, account_group: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed_assets">Fixed Assets</SelectItem>
                      <SelectItem value="current_assets">Current Assets</SelectItem>
                      <SelectItem value="bank_accounts">Bank Accounts</SelectItem>
                      <SelectItem value="cash_in_hand">Cash in Hand</SelectItem>
                      <SelectItem value="sundry_debtors">Sundry Debtors</SelectItem>
                      <SelectItem value="loans_advances">Loans & Advances</SelectItem>
                      <SelectItem value="capital_account">Capital Account</SelectItem>
                      <SelectItem value="current_liabilities">Current Liabilities</SelectItem>
                      <SelectItem value="sundry_creditors">Sundry Creditors</SelectItem>
                      <SelectItem value="duties_taxes">Duties & Taxes</SelectItem>
                      <SelectItem value="provisions">Provisions</SelectItem>
                      <SelectItem value="sales_accounts">Sales Accounts</SelectItem>
                      <SelectItem value="direct_income">Direct Income</SelectItem>
                      <SelectItem value="indirect_income">Indirect Income</SelectItem>
                      <SelectItem value="direct_expenses">Direct Expenses</SelectItem>
                      <SelectItem value="indirect_expenses">Indirect Expenses</SelectItem>
                      <SelectItem value="purchase_accounts">Purchase Accounts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Opening Balance</label>
                  <Input type="number" value={formData.opening_balance || 0} onChange={(e) => setFormData({...formData, opening_balance: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Balance Type</label>
                  <Select value={formData.opening_balance_type || 'dr'} onValueChange={(v) => setFormData({...formData, opening_balance_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dr">Debit (Dr)</SelectItem>
                      <SelectItem value="cr">Credit (Cr)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editingItem ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AccountingMasterData;
